import { Image as ExpoImage } from 'expo-image';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import type { ImagePickerAsset } from 'expo-image-picker';
import { VideoView, useVideoPlayer } from 'expo-video';
import {
  ArrowLeft,
  ArrowRight,
  Crop,
  Globe,
  ImagePlus,
  RefreshCw,
  Trash2,
  Video,
  X,
} from 'lucide-react-native';
import { createElement, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  JPEG_CONTENT_TYPE,
  assetNeedsCompatibleImagePreview,
  blobFromMediaAsset,
} from '@/src/features/media/image-upload';
import {
  isNetworkAvailable,
  isRecoverableNetworkError,
  queueCreatePost,
} from '@/src/features/offline/outbox-service';
import { getProfileAvatarURL } from '@/src/features/profile/profile-service';
import { useProfile } from '@/src/features/profile/use-profile';
import { maybeCorrectWriting } from '@/src/features/writing-assist/writing-assist-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { UserFacingError } from '@/src/utils/errors';

import { PostImageCropperModal } from './PostImageCropperModal';
import { ProfileMentionInput } from './ProfileMentionInput';
import { shouldUseFeaturedLinkPreview } from './LinkPreviewCard';
import { extractTextLinks } from './link-utils';
import {
  createPost,
  getAssetAspectRatio,
  MAX_POST_BODY_LENGTH,
  MAX_POST_PHOTO_ITEMS,
  MAX_VIDEO_DURATION_MILLIS,
  MAX_VIDEO_DURATION_SECONDS,
  preparePostMediaUpload,
  previewLinkMetadata,
  postVisibilityLabel,
  type LinkPreview,
  type PreparedPostMediaUpload,
  type PostMediaKind,
  type PostVisibility,
  type ProfileMention,
} from './post-service';

const VISIBILITY_OPTIONS: PostVisibility[] = ['public', 'friends', 'just_me'];
const PASTE_LINK_ATTACH_DELAY_MS = 350;
const FINISHED_LINK_ATTACH_DELAY_MS = 650;

type PostCreateFailurePhase =
  | 'checking network'
  | 'preparing media'
  | 'checking spelling'
  | 'uploading media'
  | 'saving post'
  | 'queueing post';

const cropOriginalAssetKey = Symbol('frienzziCropOriginalAsset');

type CroppedImagePickerAsset = ImagePickerAsset & {
  [cropOriginalAssetKey]?: ImagePickerAsset;
};

function cropSourceAsset(asset: ImagePickerAsset) {
  return (asset as CroppedImagePickerAsset)[cropOriginalAssetKey] ?? asset;
}

function withCropSourceAsset({
  croppedAsset,
  sourceAsset,
}: {
  croppedAsset: ImagePickerAsset;
  sourceAsset: ImagePickerAsset;
}) {
  Object.defineProperty(croppedAsset, cropOriginalAssetKey, {
    configurable: true,
    enumerable: false,
    value: cropSourceAsset(sourceAsset),
  });

  return croppedAsset;
}

function errorField(error: unknown, field: 'code' | 'message' | 'name') {
  if (error && typeof error === 'object') {
    const value = (error as Record<string, unknown>)[field];

    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  return null;
}

function localPostCreateError({
  error,
  payloadSummary,
  phase,
}: {
  error: unknown;
  payloadSummary: string;
  phase: PostCreateFailurePhase;
}) {
  const code = errorField(error, 'code');
  const name = errorField(error, 'name');
  const message = errorField(error, 'message') ?? String(error);
  const source = [code, name].filter(Boolean).join(' / ');

  return new UserFacingError(
    `Post failed while ${phase}${source ? ` (${source})` : ''}: ${message}. ${payloadSummary}`,
  );
}

function shouldShowLocalPostDiagnostics() {
  if (__DEV__) {
    return true;
  }

  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return false;
  }

  return ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);
}

function postCreatePayloadSummary({
  body,
  linkPreviewSourceUrl,
  mediaKind,
  preparedMediaUpload,
  preparedMediaUploads,
  profileMentions,
  visibility,
}: {
  body: string;
  linkPreviewSourceUrl: string | null | undefined;
  mediaKind: PostMediaKind | null | undefined;
  preparedMediaUpload: PreparedPostMediaUpload | null;
  preparedMediaUploads?: PreparedPostMediaUpload[];
  profileMentions: ProfileMention[];
  visibility: PostVisibility;
}) {
  const mediaSummary = preparedMediaUploads?.length
    ? [
        `media=image`,
        `count=${preparedMediaUploads.length}`,
        `sizeBytes=${preparedMediaUploads.reduce(
          (total, upload) => total + upload.sizeBytes,
          0,
        )}`,
      ].join(', ')
    : preparedMediaUpload
      ? [
          `media=${preparedMediaUpload.kind}`,
          `contentType=${preparedMediaUpload.contentType}`,
        `sizeBytes=${preparedMediaUpload.sizeBytes}`,
        `width=${preparedMediaUpload.width}`,
        `height=${preparedMediaUpload.height}`,
        `durationMillis=${preparedMediaUpload.durationMillis ?? 'null'}`,
        `fileName=${preparedMediaUpload.fileName}`,
        ].join(', ')
      : `media=${mediaKind ?? 'none'}`;

  return [
    `Debug: phase payload`,
    `bodyLength=${body.trim().length}`,
    `visibility=${visibility}`,
    `mentions=${profileMentions.length}`,
    `linkPreview=${linkPreviewSourceUrl ? 'yes' : 'no'}`,
    mediaSummary,
  ].join('; ');
}

function formatBytes(bytes: number | undefined) {
  if (!bytes) {
    return 'Size unavailable';
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDimensions(asset: ImagePickerAsset) {
  if (!asset.width || !asset.height) {
    return 'Dimensions unavailable';
  }

  return `${Math.round(asset.width)} x ${Math.round(asset.height)}`;
}

function formatMediaLabel(kind: PostMediaKind) {
  return kind === 'image' ? 'photo' : 'video';
}

function getMediaKind(asset: ImagePickerAsset | null): PostMediaKind | null {
  if (!asset) {
    return null;
  }

  return asset.type === 'video' ? 'video' : 'image';
}

function linkDisplayHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

function removeLinkFromBody(body: string, url: string) {
  const link = extractTextLinks(body).find(
    (candidate) => candidate.url === url,
  );

  if (!link) {
    return body;
  }

  const before = body.slice(0, link.start).replace(/[ \t]+$/u, '');
  const after = body.slice(link.end).replace(/^[ \t]+/u, '');

  if (!before) {
    return after.replace(/^\n+/u, '');
  }

  if (!after) {
    return before.replace(/\n+$/u, '');
  }

  const separator = before.endsWith('\n') || after.startsWith('\n') ? '' : ' ';
  return `${before}${separator}${after}`;
}

function linkHasTypedBoundary(body: string, linkEnd: number) {
  const nextCharacter = body[linkEnd];

  return typeof nextCharacter === 'string' && /\s/.test(nextCharacter);
}

function SelectedVideoPreview({ asset }: { asset: ImagePickerAsset }) {
  const player = useVideoPlayer(asset.uri);
  const aspectRatio = getAssetAspectRatio(asset);

  return (
    <VideoView
      nativeControls
      contentFit="contain"
      player={player}
      style={[styles.previewMedia, { aspectRatio }]}
    />
  );
}

function useSelectedImagePreviewURI(asset: ImagePickerAsset) {
  const [preview, setPreview] = useState<{
    failed: boolean;
    loading: boolean;
    uri: string | null;
  }>(() => ({
    failed: false,
    loading: assetNeedsCompatibleImagePreview(asset),
    uri: assetNeedsCompatibleImagePreview(asset) ? null : asset.uri,
  }));

  useEffect(() => {
    const needsCompatiblePreview = assetNeedsCompatibleImagePreview(asset);
    let active = true;

    if (!needsCompatiblePreview) {
      setPreview({
        failed: false,
        loading: false,
        uri: asset.uri,
      });
      return () => {
        active = false;
      };
    }

    setPreview({
      failed: false,
      loading: true,
      uri: null,
    });

    let objectURL: string | null = null;

    async function preparePreview() {
      if (
        Platform.OS === 'web' &&
        typeof URL !== 'undefined' &&
        typeof URL.createObjectURL === 'function'
      ) {
        try {
          const sourceBlob = await blobFromMediaAsset(
            asset,
            'That HEIC photo could not be read for preview.',
          );
          const { default: heic2any } = await import('heic2any');
          const converted = await heic2any({
            blob: sourceBlob,
            quality: 0.86,
            toType: JPEG_CONTENT_TYPE,
          });
          const convertedBlob = Array.isArray(converted)
            ? converted[0]
            : converted;

          if (!convertedBlob || convertedBlob.size <= 0) {
            throw new Error('HEIC preview conversion produced an empty image.');
          }

          objectURL = URL.createObjectURL(convertedBlob);

          if (!active) {
            URL.revokeObjectURL(objectURL);
            return;
          }

          setPreview({
            failed: false,
            loading: false,
            uri: objectURL,
          });
          return;
        } catch {
          // Fall back to Expo's image manipulator below. Native builds can often
          // convert HEIC locally even when browsers cannot decode the source.
        }
      }

      try {
        const result = await ImageManipulator.manipulateAsync(asset.uri, [], {
          compress: 0.86,
          format: ImageManipulator.SaveFormat.JPEG,
        });

        if (!active) {
          return;
        }

        setPreview({
          failed: false,
          loading: false,
          uri: result.uri,
        });
      } catch {
        if (!active) {
          return;
        }

        setPreview({
          failed: true,
          loading: false,
          uri: null,
        });
      }
    }

    void preparePreview();

    return () => {
      active = false;
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [asset]);

  return preview;
}

function SelectedImagePreviewContent({
  asset,
  contentFit = 'contain',
}: {
  asset: ImagePickerAsset;
  contentFit?: 'contain' | 'cover';
}) {
  const preview = useSelectedImagePreviewURI(asset);

  if (preview.uri) {
    if (Platform.OS === 'web') {
      return createElement('img', {
        alt: 'Selected photo preview',
        decoding: 'async',
        draggable: false,
        src: preview.uri,
        style: {
          display: 'block',
          height: '100%',
          objectFit: contentFit,
          width: '100%',
        },
      });
    }

    return (
      <ExpoImage
        accessibilityLabel="Selected photo preview"
        cachePolicy="memory-disk"
        contentFit={contentFit}
        source={{ uri: preview.uri }}
        style={styles.previewImage}
      />
    );
  }

  return (
    <View style={styles.previewFallback}>
      {preview.loading ? (
        <ActivityIndicator color={colors.primary} size="small" />
      ) : null}
      <Text style={styles.previewFallbackTitle}>
        {preview.loading ? 'Preparing preview...' : 'HEIC photo selected'}
      </Text>
      {preview.failed ? (
        <Text style={styles.previewFallbackText}>
          Could not preview this HEIC photo.
        </Text>
      ) : null}
    </View>
  );
}

function SelectedImagePreview({ asset }: { asset: ImagePickerAsset }) {
  const aspectRatio = getAssetAspectRatio(asset);

  return (
    <View style={[styles.previewFrame, { aspectRatio }]}>
      <SelectedImagePreviewContent asset={asset} />
    </View>
  );
}

function SelectedMediaPreview({
  asset,
  onChange,
  onCrop,
  onRemove,
}: {
  asset: ImagePickerAsset;
  onChange: () => void;
  onCrop?: () => void;
  onRemove: () => void;
}) {
  const mediaKind = getMediaKind(asset) ?? 'image';
  const isVideo = mediaKind === 'video';

  return (
    <View style={styles.mediaPreview}>
      {isVideo ? (
        <SelectedVideoPreview asset={asset} />
      ) : (
        <SelectedImagePreview asset={asset} />
      )}
      <View style={styles.mediaPreviewFooter}>
        <View style={styles.mediaMetaBlock}>
          <Text style={styles.mediaName} numberOfLines={1}>
            {asset.fileName ?? (isVideo ? 'Selected video' : 'Selected photo')}
          </Text>
          <Text style={styles.mediaMeta}>
            {formatBytes(asset.fileSize)} · {formatDimensions(asset)}
          </Text>
        </View>
        <View style={styles.mediaPreviewActions}>
          {!isVideo && onCrop ? (
            <Pressable
              accessibilityLabel="Crop photo"
              accessibilityRole="button"
              onPress={onCrop}
              style={styles.iconButton}
            >
              <Crop color={colors.secondary} size={18} strokeWidth={2.4} />
            </Pressable>
          ) : null}
          <Pressable
            accessibilityLabel={`Change ${isVideo ? 'video' : 'photo'}`}
            accessibilityRole="button"
            onPress={onChange}
            style={styles.iconButton}
          >
            <RefreshCw color={colors.secondary} size={18} strokeWidth={2.4} />
          </Pressable>
          <Pressable
            accessibilityLabel={`Remove ${isVideo ? 'video' : 'photo'}`}
            accessibilityRole="button"
            onPress={onRemove}
            style={[styles.iconButton, styles.removeButton]}
          >
            <Trash2 color={colors.danger} size={18} strokeWidth={2.4} />
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function SelectedPhotoListPreview({
  assets,
  onAdd,
  onCrop,
  onMove,
  onRemove,
}: {
  assets: ImagePickerAsset[];
  onAdd: () => void;
  onCrop: (index: number) => void;
  onMove: (fromIndex: number, direction: -1 | 1) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <View style={styles.photoListPreview}>
      <View style={styles.photoListHeader}>
        <View>
          <Text style={styles.mediaName}>Selected photos</Text>
          <Text style={styles.mediaMeta}>
            {assets.length}/{MAX_POST_PHOTO_ITEMS} photos
          </Text>
        </View>
        {assets.length < MAX_POST_PHOTO_ITEMS ? (
          <Pressable
            accessibilityLabel="Add more photos"
            accessibilityRole="button"
            onPress={onAdd}
            style={styles.addMorePhotosButton}
          >
            <ImagePlus color={colors.primary} size={17} strokeWidth={2.5} />
            <Text style={styles.addMorePhotosText}>Add</Text>
          </Pressable>
        ) : null}
      </View>
      <View style={styles.photoGrid}>
        {assets.map((asset, index) => (
          <View key={`${asset.uri}-${index}`} style={styles.photoGridItem}>
            <View
              style={[
                styles.photoGridImageFrame,
                { aspectRatio: getAssetAspectRatio(asset, 1) },
              ]}
            >
              <SelectedImagePreviewContent
                asset={asset}
                contentFit="contain"
              />
              <View style={styles.photoGridBadge}>
                <Text style={styles.photoGridBadgeText}>{index + 1}</Text>
              </View>
            </View>
            <View style={styles.photoGridControls}>
              <Pressable
                accessibilityLabel={`Move photo ${index + 1} earlier`}
                accessibilityRole="button"
                disabled={index === 0}
                onPress={() => onMove(index, -1)}
                style={[
                  styles.photoGridIconButton,
                  index === 0 ? styles.photoGridIconButtonDisabled : null,
                ]}
              >
                <ArrowLeft
                  color={index === 0 ? colors.mutedText : colors.secondary}
                  size={15}
                  strokeWidth={2.5}
                />
              </Pressable>
              <Pressable
                accessibilityLabel={`Move photo ${index + 1} later`}
                accessibilityRole="button"
                disabled={index === assets.length - 1}
                onPress={() => onMove(index, 1)}
                style={[
                  styles.photoGridIconButton,
                  index === assets.length - 1
                    ? styles.photoGridIconButtonDisabled
                    : null,
                ]}
              >
                <ArrowRight
                  color={
                    index === assets.length - 1
                      ? colors.mutedText
                      : colors.secondary
                  }
                  size={15}
                  strokeWidth={2.5}
                />
              </Pressable>
              <Pressable
                accessibilityLabel={`Crop photo ${index + 1}`}
                accessibilityRole="button"
                onPress={() => onCrop(index)}
                style={styles.photoGridIconButton}
              >
                <Crop color={colors.secondary} size={15} strokeWidth={2.5} />
              </Pressable>
              <Pressable
                accessibilityLabel={`Remove photo ${index + 1}`}
                accessibilityRole="button"
                onPress={() => onRemove(index)}
                style={[styles.photoGridIconButton, styles.removeButton]}
              >
                <Trash2 color={colors.danger} size={15} strokeWidth={2.5} />
              </Pressable>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function AttachedLinkPreview({
  disabled,
  loading,
  onRemove,
  onRemoveFromText,
  preview,
  url,
}: {
  disabled: boolean;
  loading: boolean;
  onRemove: () => void;
  onRemoveFromText: () => void;
  preview: LinkPreview | null;
  url: string;
}) {
  const host = linkDisplayHost(url);
  const hasReadyPreview = preview?.status === 'ready';
  const hasFeaturedPreview =
    !disabled && hasReadyPreview && shouldUseFeaturedLinkPreview(preview);
  const title = hasReadyPreview ? (preview.title ?? preview.displayUrl) : host;
  const subtitle = disabled
    ? 'Remove photo/video media to show this preview on the post.'
    : loading
      ? 'Loading preview details...'
      : hasReadyPreview
        ? (preview.description ??
          'The preview card will stay even if the link is removed from your text.')
        : 'The preview card will stay even if the link is removed from your text.';

  if (hasFeaturedPreview && preview?.imageURL) {
    return (
      <View style={styles.featuredLinkPreviewBox}>
        <View style={styles.featuredLinkPreviewImageFrame}>
          <ExpoImage
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: preview.imageURL }}
            style={styles.previewImage}
          />
        </View>
        <View style={styles.featuredLinkPreviewText}>
          <Text numberOfLines={1} style={styles.featuredLinkPreviewDomain}>
            {preview.siteName || preview.displayUrl}
          </Text>
          <Text numberOfLines={2} style={styles.featuredLinkPreviewTitle}>
            {title}
          </Text>
          <Pressable
            accessibilityRole="button"
            onPress={onRemoveFromText}
            style={styles.featuredRemoveLinkTextButton}
          >
            <Text style={styles.removeLinkText}>Remove link from text</Text>
          </Pressable>
        </View>
        <Pressable
          accessibilityLabel="Remove link preview"
          accessibilityRole="button"
          onPress={onRemove}
          style={styles.featuredRemoveLinkButton}
        >
          <X color={colors.mutedText} size={22} strokeWidth={2.5} />
        </Pressable>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.linkPreviewBox,
        disabled ? styles.linkPreviewBoxDisabled : null,
      ]}
    >
      {hasReadyPreview && preview.imageURL ? (
        <ExpoImage
          accessibilityLabel=""
          cachePolicy="memory-disk"
          contentFit="cover"
          source={{ uri: preview.imageURL }}
          style={styles.linkPreviewImage}
        />
      ) : (
        <View style={styles.linkPreviewIcon}>
          {loading ? (
            <ActivityIndicator color={colors.secondary} size="small" />
          ) : (
            <Globe color={colors.secondary} size={22} strokeWidth={2.3} />
          )}
        </View>
      )}
      <View style={styles.linkPreviewText}>
        <Text style={styles.linkPreviewEyebrow}>
          {disabled ? 'Link preview disabled' : 'Link preview attached'}
        </Text>
        <Text numberOfLines={1} style={styles.linkPreviewTitle}>
          {title}
        </Text>
        <Text numberOfLines={1} style={styles.linkPreviewSubtitle}>
          {subtitle}
        </Text>
        {hasReadyPreview && preview.siteName ? (
          <Text numberOfLines={1} style={styles.linkPreviewDomain}>
            {preview.siteName} · {preview.displayUrl}
          </Text>
        ) : null}
        {!disabled ? (
          <Pressable
            accessibilityRole="button"
            onPress={onRemoveFromText}
            style={styles.removeLinkTextButton}
          >
            <Text style={styles.removeLinkText}>Remove link from text</Text>
          </Pressable>
        ) : null}
      </View>
      <Pressable
        accessibilityLabel="Remove link preview"
        accessibilityRole="button"
        onPress={onRemove}
        style={styles.removeLinkButton}
      >
        <X color={colors.mutedText} size={18} strokeWidth={2.5} />
      </Pressable>
    </View>
  );
}

function profileInitial(name: string | null | undefined, email: string | null | undefined) {
  const source = name?.trim() || email?.trim() || 'F';
  return source.slice(0, 1).toUpperCase();
}

function ComposerAuthorRow({
  email,
  fullName,
  photoURL,
  visibility,
  onVisibilityChange,
}: {
  email: string | null | undefined;
  fullName: string | null | undefined;
  photoURL: string | null;
  visibility: PostVisibility;
  onVisibilityChange: (visibility: PostVisibility) => void;
}) {
  return (
    <View style={styles.composerTopPanel}>
      <View style={styles.composerAuthorRow}>
        <View style={styles.composerAvatar}>
          {photoURL ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              source={{ uri: photoURL }}
              style={styles.composerAvatarImage}
            />
          ) : (
            <Text style={styles.composerAvatarText}>
              {profileInitial(fullName, email)}
            </Text>
          )}
        </View>
        <View style={styles.composerAuthorText}>
          <Text numberOfLines={1} style={styles.composerAuthorName}>
            {fullName?.trim() || 'Frienzzi member'}
          </Text>
          {email ? (
            <Text numberOfLines={1} style={styles.composerAuthorMeta}>
              {email}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.visibilityCompactOptions}>
        {VISIBILITY_OPTIONS.map((option) => {
          const selected = visibility === option;

          return (
            <Pressable
              key={option}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              onPress={() => onVisibilityChange(option)}
              style={[
                styles.visibilityCompactOption,
                selected ? styles.visibilityCompactOptionSelected : null,
              ]}
            >
              <Text
                style={[
                  styles.visibilityCompactOptionText,
                  selected ? styles.visibilityCompactOptionTextSelected : null,
                ]}
              >
                {postVisibilityLabel(option)}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <Text style={styles.visibilityHelp}>
        {visibility === 'public'
          ? 'Public posts can be shared and viewed from public post links.'
          : visibility === 'friends'
            ? 'Friends posts are visible to mutual follows only.'
            : 'Just Me posts are visible only to you.'}
      </Text>
    </View>
  );
}

function PostingStatusStrip({
  progress,
  status,
}: {
  progress: number | null;
  status: string | null;
}) {
  if (!status) {
    return null;
  }

  const normalizedProgress =
    typeof progress === 'number'
      ? Math.max(0, Math.min(1, progress))
      : null;

  return (
    <View style={styles.postingStatusStrip}>
      <View style={styles.postingStatusRow}>
        <ActivityIndicator color={colors.primary} size="small" />
        <Text numberOfLines={2} style={styles.postingStatusText}>
          {status}
        </Text>
      </View>
      {normalizedProgress != null ? (
        <View style={styles.postingProgressTrack}>
          <View
            style={[
              styles.postingProgressFill,
              { width: `${Math.round(normalizedProgress * 100)}%` },
            ]}
          />
        </View>
      ) : null}
    </View>
  );
}

export function PostCreateComposer({
  onCancel,
  onComplete,
  presentation = 'screen',
}: {
  onCancel: () => void;
  onComplete: () => void;
  presentation?: 'modal' | 'screen';
}) {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const profileQuery = useProfile(user?.uid);
  const [body, setBody] = useState('');
  const [profileMentions, setProfileMentions] = useState<ProfileMention[]>([]);
  const [mediaAsset, setMediaAsset] = useState<ImagePickerAsset | null>(null);
  const [photoAssets, setPhotoAssets] = useState<ImagePickerAsset[]>([]);
  const [attachedLinkUrl, setAttachedLinkUrl] = useState<string | null>(null);
  const [attachedLinkPreview, setAttachedLinkPreview] =
    useState<LinkPreview | null>(null);
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const [dismissedLinkUrls, setDismissedLinkUrls] = useState<Set<string>>(
    () => new Set(),
  );
  const previousBodyRef = useRef('');
  const [lastBodyEdit, setLastBodyEdit] = useState({
    pasteLike: false,
    sequence: 0,
  });
  const [visibility, setVisibility] = useState<PostVisibility>('public');
  const [posting, setPosting] = useState(false);
  const [postingStatus, setPostingStatus] = useState<string | null>(null);
  const [postingProgress, setPostingProgress] = useState<number | null>(null);
  const [cropTarget, setCropTarget] = useState<
    | {
        index: number;
        kind: 'photo-list';
      }
    | {
        kind: 'single';
      }
    | null
  >(null);

  const mediaKind = photoAssets.length > 0 ? 'image' : getMediaKind(mediaAsset);
  const mediaLabel = mediaKind ? formatMediaLabel(mediaKind) : null;
  const hasMedia = photoAssets.length > 0 || Boolean(mediaAsset);
  const linkPreviewSourceUrl = hasMedia ? null : attachedLinkUrl;
  const canPost =
    Boolean(body.trim() || hasMedia || linkPreviewSourceUrl) && !posting;
  const selectedCropAsset =
    cropTarget?.kind === 'single'
      ? mediaAsset
      : cropTarget?.kind === 'photo-list'
        ? (photoAssets[cropTarget.index] ?? null)
        : null;
  const cropAsset = selectedCropAsset ? cropSourceAsset(selectedCropAsset) : null;

  useEffect(() => {
    if (attachedLinkUrl) {
      return;
    }

    const firstLink = extractTextLinks(body).find(
      (link) => !dismissedLinkUrls.has(link.url),
    );

    if (!firstLink) {
      return;
    }

    const linkIsComplete =
      lastBodyEdit.pasteLike || linkHasTypedBoundary(body, firstLink.end);

    if (!linkIsComplete) {
      return;
    }

    const timeout = setTimeout(
      () => setAttachedLinkUrl(firstLink.url),
      lastBodyEdit.pasteLike
        ? PASTE_LINK_ATTACH_DELAY_MS
        : FINISHED_LINK_ATTACH_DELAY_MS,
    );

    return () => clearTimeout(timeout);
  }, [attachedLinkUrl, body, dismissedLinkUrls, lastBodyEdit]);

  useEffect(() => {
    if (!attachedLinkUrl) {
      setAttachedLinkPreview(null);
      setLinkPreviewLoading(false);
      return;
    }

    let active = true;

    setAttachedLinkPreview(null);
    setLinkPreviewLoading(true);

    previewLinkMetadata(attachedLinkUrl)
      .then((preview) => {
        if (!active) {
          return;
        }

        setAttachedLinkPreview(preview?.status === 'ready' ? preview : null);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setAttachedLinkPreview(null);
      })
      .finally(() => {
        if (active) {
          setLinkPreviewLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [attachedLinkUrl]);

  function handleBodyChange(nextBody: string) {
    const previousBody = previousBodyRef.current;
    const lengthDelta = Math.abs(nextBody.length - previousBody.length);

    previousBodyRef.current = nextBody;
    setLastBodyEdit((currentEdit) => ({
      pasteLike: lengthDelta >= 8,
      sequence: currentEdit.sequence + 1,
    }));
    setBody(nextBody);
  }

  function resetComposer() {
    previousBodyRef.current = '';
    setBody('');
    setProfileMentions([]);
    setMediaAsset(null);
    setPhotoAssets([]);
    setAttachedLinkUrl(null);
    setAttachedLinkPreview(null);
    setLinkPreviewLoading(false);
    setDismissedLinkUrls(new Set());
    setLastBodyEdit((currentEdit) => ({
      pasteLike: false,
      sequence: currentEdit.sequence + 1,
    }));
    setVisibility('public');
    setPostingStatus(null);
    setPostingProgress(null);
    setCropTarget(null);
  }

  async function chooseMedia(kind: PostMediaKind) {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (!permission.granted) {
      showError(
        new Error('Photo library access is required to add media to a post.'),
      );
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      allowsEditing: false,
      allowsMultipleSelection: kind === 'image',
      mediaTypes: [kind === 'image' ? 'images' : 'videos'],
      preferredAssetRepresentationMode:
        kind === 'image'
          ? ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible
          : undefined,
      quality: 0.85,
      selectionLimit:
        kind === 'image'
          ? Math.max(MAX_POST_PHOTO_ITEMS - photoAssets.length, 1)
          : 1,
      videoMaxDuration: MAX_VIDEO_DURATION_SECONDS,
    });

    if (result.canceled) {
      return;
    }

    const asset = result.assets[0] ?? null;

    if (
      kind === 'video' &&
      asset?.duration != null &&
      asset.duration > MAX_VIDEO_DURATION_MILLIS
    ) {
      showError(new Error('Videos must be 10 minutes or shorter.'));
      return;
    }

    if (kind === 'image') {
      const selectedPhotos = result.assets.filter(
        (candidate) => (candidate.type ?? 'image') !== 'video',
      );
      setMediaAsset(null);
      setPhotoAssets((currentPhotos) =>
        [...currentPhotos, ...selectedPhotos].slice(0, MAX_POST_PHOTO_ITEMS),
      );
      return;
    }

    setPhotoAssets([]);
    setMediaAsset(asset);
  }

  function attachPastedImage(asset: ImagePickerAsset) {
    setMediaAsset(null);
    setPhotoAssets((currentPhotos) =>
      [...currentPhotos, asset].slice(0, MAX_POST_PHOTO_ITEMS),
    );
    setAttachedLinkUrl(null);
    setAttachedLinkPreview(null);
    setLinkPreviewLoading(false);
    trackEvent('post_media_paste', { media_kind: 'image' });
  }

  function movePhoto(fromIndex: number, direction: -1 | 1) {
    setPhotoAssets((currentPhotos) => {
      const toIndex = fromIndex + direction;

      if (toIndex < 0 || toIndex >= currentPhotos.length) {
        return currentPhotos;
      }

      const nextPhotos = [...currentPhotos];
      const [photo] = nextPhotos.splice(fromIndex, 1);
      nextPhotos.splice(toIndex, 0, photo);
      return nextPhotos;
    });
  }

  async function submitPost() {
    if (!user) {
      showError(new Error('You must be signed in to create a post.'));
      return;
    }

    setPosting(true);
    setPostingProgress(null);
    setPostingStatus(
      hasMedia && mediaLabel
        ? `Preparing ${mediaLabel}...`
        : 'Publishing post...',
    );

    let finalBody = body.trim();
    const postInput = {
      authorUid: user.uid,
      authorFullName: profileQuery.data?.fullName ?? null,
      body: finalBody,
      linkPreviewSourceUrl,
      profileMentions,
      visibility,
      mediaAsset: photoAssets.length > 0 ? null : mediaAsset,
      mediaAssets: photoAssets,
      mediaKind,
    };
    let failurePhase: PostCreateFailurePhase = 'checking network';
    let preparedMediaUpload: PreparedPostMediaUpload | null = null;
    let preparedMediaUploads: PreparedPostMediaUpload[] = [];

    try {
      if (!(await isNetworkAvailable())) {
        failurePhase = 'queueing post';
        setPostingStatus('Queueing post for upload...');
        await queueCreatePost(postInput);
        trackEvent('post_queue', {
          has_media: hasMedia,
          has_text: Boolean(body.trim()),
          media_kind: mediaKind ?? 'text',
          media_count: photoAssets.length || (mediaAsset ? 1 : 0),
          visibility,
        });
        showSuccess(
          'Your post will upload when your connection is back.',
          'Post queued',
        );
        resetComposer();
        onComplete();
        return;
      }

      if (photoAssets.length > 0) {
        failurePhase = 'preparing media';
        setPostingStatus(`Preparing ${photoAssets.length} photos...`);
        preparedMediaUploads = [];

        for (const [index, photoAsset] of photoAssets.entries()) {
          setPostingStatus(
            `Preparing photo ${index + 1} of ${photoAssets.length}...`,
          );
          preparedMediaUploads.push(
            await preparePostMediaUpload(photoAsset, 'image'),
          );
        }
      } else if (mediaAsset && mediaKind) {
        failurePhase = 'preparing media';
        setPostingStatus(`Preparing ${mediaLabel ?? 'media'}...`);
        preparedMediaUpload = await preparePostMediaUpload(
          mediaAsset,
          mediaKind,
        );
      }

      if (finalBody) {
        failurePhase = 'checking spelling';
        setPostingStatus('Checking spelling...');

        try {
          const correction = await maybeCorrectWriting({
            profile: profileQuery.data,
            surface: 'post',
            text: finalBody,
          });

          finalBody = correction.text;
          postInput.body = finalBody;

          if (correction.changed) {
            trackEvent('writing_assist_correction_apply', {
              surface: 'post',
            });
          }
        } catch {
          trackEvent('writing_assist_correction_skip', {
            reason: 'error',
            surface: 'post',
          });
        }

        setPostingStatus(
          hasMedia && mediaLabel
            ? `Preparing ${mediaLabel}...`
            : 'Publishing post...',
        );
      }

      failurePhase = hasMedia ? 'uploading media' : 'saving post';
      await createPost({
        ...postInput,
        debugDiagnostics: shouldShowLocalPostDiagnostics(),
        preparedMediaUpload,
        preparedMediaUploads,
        onUploadProgress:
          hasMedia && mediaLabel
            ? (progress) => {
                if (progress >= 1) {
                  failurePhase = 'saving post';
                }

                setPostingProgress(progress);
                setPostingStatus(
                  `Uploading ${
                    photoAssets.length > 1
                      ? `${photoAssets.length} photos`
                      : mediaLabel
                  } ${Math.round(progress * 100)}%`,
                );
              }
            : undefined,
      });
      trackEvent('post_create', {
        has_media: hasMedia,
        has_text: Boolean(body.trim()),
        media_kind: mediaKind ?? 'text',
        media_count: photoAssets.length || (mediaAsset ? 1 : 0),
        visibility,
      });
      showSuccess('Your post is live.', 'Post shared');
      resetComposer();
      onComplete();
    } catch (caughtError) {
      if (isRecoverableNetworkError(caughtError)) {
        failurePhase = 'queueing post';
        setPostingStatus('Queueing post for upload...');
        await queueCreatePost(postInput);
        showSuccess(
          'Your post will upload when your connection is back.',
          'Post queued',
        );
        resetComposer();
        onComplete();
        return;
      }

      if (shouldShowLocalPostDiagnostics()) {
        const payloadSummary = postCreatePayloadSummary({
          body: postInput.body,
          linkPreviewSourceUrl,
          mediaKind,
          preparedMediaUpload,
          preparedMediaUploads,
          profileMentions,
          visibility,
        });
        const diagnosticError = localPostCreateError({
          error: caughtError,
          payloadSummary,
          phase: failurePhase,
        });

        console.error(diagnosticError.message);
        console.error('Post create failed.', {
          error: caughtError,
          hasMedia,
          hasText: Boolean(body.trim()),
          mediaKind: mediaKind ?? 'text',
          payloadSummary,
          phase: failurePhase,
          visibility,
        });
        showError(diagnosticError);
      } else {
        showError(caughtError, 'Unable to create your post. Please try again.');
      }
    } finally {
      setPosting(false);
      setPostingStatus(null);
      setPostingProgress(null);
    }
  }

  const profile = profileQuery.data;
  const avatarURL = getProfileAvatarURL(profile, 'thumb');
  const content = (
    <View style={styles.form}>
      <ComposerAuthorRow
        email={profile?.email ?? user?.email}
        fullName={profile?.fullName}
        photoURL={avatarURL}
        visibility={visibility}
        onVisibilityChange={setVisibility}
      />
      <ProfileMentionInput
        label="Post"
        autoCapitalize="sentences"
        maxLength={MAX_POST_BODY_LENGTH}
        mentions={profileMentions}
        multiline
        numberOfLines={6}
        onChangeText={handleBodyChange}
        onMentionsChange={setProfileMentions}
        onPasteImageAsset={attachPastedImage}
        placeholder="What do you want to share?"
        scope={{ kind: 'post' }}
        showLabel={false}
        style={styles.bodyInput}
        textAlignVertical="top"
        value={body}
      />
      <Text style={styles.counter}>
        {body.length}/{MAX_POST_BODY_LENGTH}
      </Text>
      <View style={styles.mediaActionRow}>
        <Pressable
          accessibilityLabel="Add photo"
          accessibilityRole="button"
          disabled={posting}
          onPress={() => {
            void chooseMedia('image');
          }}
          style={({ pressed }) => [
            styles.mediaActionButton,
            pressed && !posting ? styles.mediaActionButtonPressed : null,
          ]}
        >
          <ImagePlus color={colors.primary} size={20} strokeWidth={2.4} />
          <Text style={styles.mediaActionText}>
            {photoAssets.length > 0 ? 'Add photos' : 'Photos'}
          </Text>
        </Pressable>
        <Pressable
          accessibilityLabel="Add video"
          accessibilityRole="button"
          disabled={posting}
          onPress={() => {
            void chooseMedia('video');
          }}
          style={({ pressed }) => [
            styles.mediaActionButton,
            pressed && !posting ? styles.mediaActionButtonPressed : null,
          ]}
        >
          <Video color={colors.accent} size={20} strokeWidth={2.4} />
          <Text style={styles.mediaActionText}>
            {mediaKind === 'video' ? 'Change video' : 'Video'}
          </Text>
        </Pressable>
      </View>
      {photoAssets.length > 0 ? (
        <SelectedPhotoListPreview
          assets={photoAssets}
          onAdd={() => {
            void chooseMedia('image');
          }}
          onCrop={(index) => setCropTarget({ index, kind: 'photo-list' })}
          onMove={movePhoto}
          onRemove={(index) => {
            setPhotoAssets((currentPhotos) =>
              currentPhotos.filter((_, currentIndex) => currentIndex !== index),
            );
          }}
        />
      ) : mediaAsset ? (
        <SelectedMediaPreview
          asset={mediaAsset}
          onChange={() => {
            const nextKind = getMediaKind(mediaAsset) ?? 'image';
            void chooseMedia(nextKind);
          }}
          onCrop={() => setCropTarget({ kind: 'single' })}
          onRemove={() => setMediaAsset(null)}
        />
      ) : null}
      {attachedLinkUrl ? (
        <AttachedLinkPreview
          disabled={hasMedia}
          loading={linkPreviewLoading}
          preview={attachedLinkPreview}
          url={attachedLinkUrl}
          onRemove={() => {
            setDismissedLinkUrls((currentDismissedUrls) => {
              const nextDismissedUrls = new Set(currentDismissedUrls);
              nextDismissedUrls.add(attachedLinkUrl);
              return nextDismissedUrls;
            });
            setAttachedLinkUrl(null);
            setAttachedLinkPreview(null);
            setLinkPreviewLoading(false);
          }}
          onRemoveFromText={() => {
            setBody((currentBody) =>
              removeLinkFromBody(currentBody, attachedLinkUrl),
            );
          }}
        />
      ) : null}
    </View>
  );
  const footer = (
    <View style={styles.footer}>
      <PostingStatusStrip progress={postingProgress} status={postingStatus} />
      <View style={styles.actions}>
        <Button
          title="Cancel"
          variant="secondary"
          disabled={posting}
          onPress={() => {
            resetComposer();
            onCancel();
          }}
          style={styles.actionButton}
        />
        <Button
          title="Post"
          disabled={!canPost || posting}
          onPress={submitPost}
          style={styles.actionButton}
        />
      </View>
    </View>
  );

  if (presentation === 'modal') {
    return (
      <View style={styles.modalComposer}>
        <ScrollView
          contentContainerStyle={styles.modalContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          style={styles.modalScroll}
        >
          {content}
        </ScrollView>
        {footer}
        <PostImageCropperModal
          asset={cropAsset}
          visible={Boolean(cropAsset && cropTarget)}
          onCancel={() => setCropTarget(null)}
          onComplete={(croppedAsset) => {
            const target = cropTarget;
            const completedAsset = cropAsset
              ? withCropSourceAsset({
                  croppedAsset,
                  sourceAsset: cropAsset,
                })
              : croppedAsset;

            if (target?.kind === 'single') {
              setMediaAsset(completedAsset);
            } else if (target?.kind === 'photo-list') {
              setPhotoAssets((currentPhotos) =>
                currentPhotos.map((photo, index) =>
                  index === target.index ? completedAsset : photo,
                ),
              );
            }

            setCropTarget(null);
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.screenComposer}>
      {content}
      {footer}
      <PostImageCropperModal
        asset={cropAsset}
        visible={Boolean(cropAsset && cropTarget)}
        onCancel={() => setCropTarget(null)}
        onComplete={(croppedAsset) => {
          const target = cropTarget;
          const completedAsset = cropAsset
            ? withCropSourceAsset({
                croppedAsset,
                sourceAsset: cropAsset,
              })
            : croppedAsset;

          if (target?.kind === 'single') {
            setMediaAsset(completedAsset);
          } else if (target?.kind === 'photo-list') {
            setPhotoAssets((currentPhotos) =>
              currentPhotos.map((photo, index) =>
                index === target.index ? completedAsset : photo,
              ),
            );
          }

          setCropTarget(null);
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    flex: 1,
  },
  addMorePhotosButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 36,
    paddingHorizontal: spacing.sm,
  },
  addMorePhotosText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: '900',
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  bodyInput: {
    minHeight: 132,
  },
  composerAuthorMeta: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
  },
  composerAuthorName: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  composerAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  composerAuthorText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  composerAvatar: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 22,
    borderWidth: StyleSheet.hairlineWidth,
    height: 44,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 44,
  },
  composerAvatarImage: {
    height: '100%',
    width: '100%',
  },
  composerAvatarText: {
    color: colors.primary,
    fontSize: 17,
    fontWeight: '900',
  },
  composerTopPanel: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    padding: spacing.sm,
  },
  counter: {
    alignSelf: 'flex-end',
    color: colors.mutedText,
    fontSize: 13,
  },
  form: {
    gap: spacing.sm,
  },
  footer: {
    backgroundColor: colors.surface,
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  iconButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  featuredLinkPreviewBox: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  featuredLinkPreviewDomain: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  featuredLinkPreviewImageFrame: {
    aspectRatio: 1.91,
    backgroundColor: colors.background,
    width: '100%',
  },
  featuredLinkPreviewText: {
    backgroundColor: colors.surfaceTint,
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  featuredLinkPreviewTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
    lineHeight: 22,
  },
  featuredRemoveLinkButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 19,
    borderWidth: StyleSheet.hairlineWidth,
    height: 38,
    justifyContent: 'center',
    position: 'absolute',
    right: 8,
    top: 8,
    width: 38,
  },
  featuredRemoveLinkTextButton: {
    alignSelf: 'flex-start',
  },
  linkPreviewBox: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  linkPreviewBoxDisabled: {
    opacity: 0.65,
  },
  linkPreviewEyebrow: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  linkPreviewDomain: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    paddingTop: 2,
  },
  linkPreviewIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  linkPreviewImage: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    height: 72,
    width: 72,
  },
  linkPreviewSubtitle: {
    color: colors.mutedText,
    fontSize: 12,
    lineHeight: 16,
  },
  linkPreviewText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  linkPreviewTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  mediaActionButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    justifyContent: 'center',
    minHeight: 42,
    paddingHorizontal: spacing.md,
  },
  mediaActionButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  mediaActionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  mediaActionText: {
    color: colors.secondary,
    fontSize: 14,
    fontWeight: '900',
  },
  mediaMeta: {
    color: colors.mutedText,
    fontSize: 13,
  },
  mediaMetaBlock: {
    flex: 1,
    gap: 2,
  },
  mediaName: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '800',
  },
  mediaPreview: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    overflow: 'hidden',
  },
  mediaPreviewActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  mediaPreviewFooter: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  photoGridBadge: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.72)',
    borderRadius: 12,
    height: 24,
    justifyContent: 'center',
    left: 6,
    position: 'absolute',
    top: 6,
    width: 24,
  },
  photoGridBadgeText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  photoGridControls: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 3,
    justifyContent: 'center',
    paddingTop: spacing.xs,
  },
  photoGridIconButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 13,
    borderWidth: 1,
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  photoGridIconButtonDisabled: {
    opacity: 0.42,
  },
  photoGridImageFrame: {
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  photoGridItem: {
    maxWidth: 120,
    minWidth: 94,
    width: '31%',
  },
  photoListHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  photoListPreview: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    padding: spacing.sm,
  },
  modalComposer: {
    flexShrink: 1,
    maxHeight: '100%',
  },
  modalContent: {
    padding: spacing.md,
    paddingTop: spacing.sm,
  },
  modalScroll: {
    flexGrow: 0,
    flexShrink: 1,
  },
  postingProgressFill: {
    backgroundColor: colors.primary,
    borderRadius: 999,
    height: '100%',
  },
  postingProgressTrack: {
    backgroundColor: colors.surfacePressed,
    borderRadius: 999,
    height: 4,
    overflow: 'hidden',
    width: '100%',
  },
  postingStatusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  postingStatusStrip: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  postingStatusText: {
    color: colors.text,
    flex: 1,
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 17,
  },
  previewFrame: {
    backgroundColor: colors.surfaceTint,
    width: '100%',
  },
  previewFallback: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    gap: spacing.xs,
    height: '100%',
    justifyContent: 'center',
    padding: spacing.md,
    width: '100%',
  },
  previewFallbackText: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16,
    textAlign: 'center',
  },
  previewFallbackTitle: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '900',
    lineHeight: 17,
    textAlign: 'center',
  },
  previewImage: {
    height: '100%',
    width: '100%',
  },
  previewMedia: {
    backgroundColor: colors.text,
    width: '100%',
  },
  removeButton: {
    backgroundColor: '#fff4f7',
  },
  removeLinkButton: {
    alignItems: 'center',
    borderRadius: 17,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  removeLinkText: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  removeLinkTextButton: {
    alignSelf: 'flex-start',
    paddingTop: spacing.xs,
  },
  screenComposer: {
    gap: spacing.md,
  },
  visibilityCompactOption: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 34,
    paddingHorizontal: spacing.sm,
  },
  visibilityCompactOptionSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  visibilityCompactOptionText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  visibilityCompactOptionTextSelected: {
    color: colors.surface,
  },
  visibilityCompactOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  visibilityHelp: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
});
